import { useSearch } from "@tanstack/react-router";
import { Button } from "antd";
import { useGetCascadeQuery } from "api/TenantSlug/Cascade/hooks";
import { CascadeFilterDrawerBody } from "components/Drawers/CascadeFilterDrawer/CascadeFilterDrawerBody.tsx";
import { useCloseAfterSave } from "components/UI/BaseFilter/hooks/useCloseAfterSave";
import { useConfirmedEntityId } from "components/UI/BaseFilter/hooks/useConfirmedEntityId";
import { useUnsavedChangesGuard } from "components/UI/BaseFilter/hooks/useUnsavedChangesGuard";
import { Drawer } from "components/UI/Drawer";
import { DrawerTitle } from "components/UI/DrawerTitle/DrawerTitle.tsx";
import { useDrawer } from "hooks/useDrawer";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { DRAWER_WIDTH } from "localConstants";
import { useState } from "react";
import { DrawerId, FormId } from "types/models.ts";
import { getDrawerSearchParams } from "utils/drawerHelpers";

interface SearchParams {
    [`cascadeFilter-id`]: string;
}

interface CascadeFilterDrawerProps {
    /** Called after the filter is saved, e.g. to reload a table that shows it */
    onFilterSaved?: () => void | Promise<void>;
}

export const CascadeFilterDrawer = ({ onFilterSaved }: CascadeFilterDrawerProps) => {
    const drawerId = DrawerId.CascadeFilter;
    const searchParams = useSearch({ strict: false }) as SearchParams;
    const requestedCascadeId = searchParams[`${drawerId}-id`];
    const { t } = usePaldockTranslation("drawers.cascade-filter");
    const { t: tC } = usePaldockTranslation("common");
    const drawerUtils = useDrawer();

    // Owned here, not in the (per-cascadeId, remounted) body below — these describe the currently open
    // channel's editor from the outside, for the Drawer chrome (Save button, close confirmation), which
    // must stay a single, continuously mounted instance regardless of which channel's filter is showing.
    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const { confirmDiscard } = useUnsavedChangesGuard(isDirty);

    // Clicking another channel's filter only changes the URL param; with unsaved changes the current channel
    // stays until the user confirms discarding them, otherwise the URL is put back.
    const cascadeId = useConfirmedEntityId({
        id: requestedCascadeId,
        isDirty,
        confirmDiscard,
        onReject: (confirmedId) => drawerUtils.show(drawerId, { [`${drawerId}-id`]: confirmedId }),
    });

    // Closes after a save, but not when the drawer meanwhile switched to another cascade or has new unsaved edits
    const handleSaved = useCloseAfterSave({
        shownId: cascadeId,
        isDirty,
        close: () => drawerUtils.hide(drawerId, getDrawerSearchParams(drawerId, "id")),
    });

    // Same query (key and request) as the body, so the channel name comes from its cache
    const cascadeQuery = useGetCascadeQuery(
        cascadeId ?? "",
        { filter: { include: "integration" } },
        { enabled: Boolean(cascadeId) }
    );
    const channelTitle = cascadeQuery.data?.cascade?.title;

    if (!cascadeId) {
        return null;
    }

    return (
        <Drawer
            drawerId={drawerId}
            title={<DrawerTitle mainTitle={channelTitle ? t("title_named", { title: channelTitle }) : t("title")} />}
            searchParamsToClean={getDrawerSearchParams(drawerId, "id")}
            width={Number(DRAWER_WIDTH) + 100}
            onBeforeClose={() => !isDirty || confirmDiscard()}
            extraButtons={
                <Button
                    size="large"
                    type="primary"
                    htmlType="submit"
                    form={FormId.CascadeFilterForm}
                    loading={isSaving}
                >
                    {tC("save")}
                </Button>
            }
        >
            <div>
                <CascadeFilterDrawerBody
                    key={cascadeId}
                    cascadeId={cascadeId}
                    onDirtyChange={setIsDirty}
                    onSavingChange={setIsSaving}
                    onSaved={handleSaved}
                    onFilterSaved={onFilterSaved}
                />
            </div>
        </Drawer>
    );
};
